import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import * as authApi from "../api/auth";

/**
 * AuthContext — app-level authentication state.
 *
 * `user` is one of:
 *   - undefined  → still checking session (/api/auth/me in-flight)
 *   - null       → not authenticated
 *   - { email, is_new, created_at }
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  // Initial session check
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await authApi.me();
        if (!cancelled) setUser(data);
      } catch (_e) {
        if (!cancelled) setUser(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    setUser(data);
    return data;
  }, []);

  const signUp = useCallback(async (email, password) => {
    const data = await authApi.register(email, password);
    setUser(data);
    return data;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
    }
  }, []);

  // Merge a partial update (e.g. after saving profile settings) into the
  // current user object without re-fetching /auth/me.
  const updateUser = useCallback((partial) => {
    setUser((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  // Auto-logout on any 401 from the API (e.g. cookie expired)
  // Also handle 403 for revoked users when checking auth
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const isAuthEndpoint = err?.config?.url?.includes("/auth/me");
        
        // Handle 401 (unauthorized) - always logout if user is logged in
        if (status === 401 && user) {
          setUser(null);
        }
        
        // Handle 403 (forbidden/revoked) on auth check endpoint
        if (status === 403 && user && isAuthEndpoint) {
          setUser(null);
        }
        
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, signIn, signUp, signOut, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
